import { useNavigate } from 'react-router-dom';
import React from 'react';
import BadgeBoolean from '../../../components/BadgeBoolean';
import { UseQueryResult } from 'react-query';
import { Profile, ProfileListResponse } from '../../../interfaces/profile';
import { match } from 'ts-pattern';
import TableLoading from '../../../components/Layout/Loading/TableLoading';
import { PencilIcon } from '@heroicons/react/outline';
import IconLink from '../../../components/TextLink/IconLink';
import TableHeader from '../../../components/Table/TableHeader';
import Checkbox from '../../../components/Form/Checkbox';

interface AnzianitaTableProps {
  query: UseQueryResult<ProfileListResponse>;
  selectedIds: Set<number>;
  onToggle: (id: number, checked: boolean) => void;
  /** Seleziona / deseleziona tutti gli utenti della pagina corrente (header tabella). */
  onSelectAllCurrentPage: (select: boolean) => void;
}

const AnzianitaTable: React.FC<AnzianitaTableProps> = ({
  query,
  selectedIds,
  onToggle,
  onSelectAllCurrentPage,
}) => {
  const tableElements = [
    { key: null, label: 'Tessera' },
    {
      key: 'surname',
      label: 'Cognome',
      class: 'px-3 py-4 text-sm text-gray-500 w-[120px] sticky left-0',
    },
    {
      key: 'name',
      label: 'Nome',
      class: 'px-3 py-4 text-sm text-gray-500 w-[120px] sticky left-[120px]',
    },
    { key: null, label: 'Email' },
    { key: null, label: 'Anni anzianità' },
    { key: null, label: 'Spilla 10 anni' },
    { key: null, label: 'Spilla 25 anni' },
    { key: null, label: 'Spilla 50 anni' },
    { key: null, label: 'Sezione' },
    { key: null, label: 'Delegazione regionale di appartenenza' },
    { key: null, label: '' },
  ];

  const navigate = useNavigate();

  return (
    <div className='-my-2 overflow-x-auto'>
      <div className='inline-block min-w-full py-2 align-middle'>
        <div className='overflow-hidden ring-1 border ring-black ring-opacity-5 '>
          {match(query)
            .with({ status: 'success' }, (q) => {
              if (!q.data) {
                return (
                  <div className='text-gray-500 my-20 text-center'>
                    Non hai i permessi
                  </div>
                );
              }
              const rows = q.data.data;
              const pageIds = rows.map((u) => u.id);
              const allPageSelected =
                pageIds.length > 0 &&
                pageIds.every((id) => selectedIds.has(id));

              return (
                <table className='min-w-full divide-y divide-gray-300'>
                  <TableHeader
                    columnsNames={tableElements}
                    checked={allPageSelected}
                    onCheckAll={(e) => {
                      e.stopPropagation();
                      onSelectAllCurrentPage(e.target.checked);
                    }}
                  />
                  <tbody className='divide-y divide-gray-200 bg-white'>
                    {rows.map((user) => (
                      <tr
                        key={user.id}
                        onClick={() => navigate('/utenti/modifica/' + user.id)}
                        className='hover:cursor-pointer hover:bg-neutral-100'
                      >
                        <td
                          className='whitespace-nowrap px-6 py-4 text-sm text-gray-600'
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Checkbox
                            checked={selectedIds.has(user.id)}
                            onChange={(e) => onToggle(user.id, e.target.checked)}
                            aria-label={`Seleziona ${user.attributes.surname}`}
                          />
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-600'>
                          {
                            user.attributes.badge?.data?.attributes
                              .badgeNumber
                          }
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                          {user.attributes.surname}
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                          {user.attributes.name}
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                          {user.attributes.user?.data?.attributes.email}
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                          {user.attributes.yearsInONAV ?? 0}
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                          <BadgeBoolean
                            active={Boolean(user.attributes.pin10YearsDelivered)}
                          />
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                          <BadgeBoolean
                            active={Boolean(user.attributes.pin25YearsDelivered)}
                          />
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                          <BadgeBoolean
                            active={Boolean(user.attributes.pin50YearsDelivered)}
                          />
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                          {user.attributes.section?.data?.attributes.name}
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500'>
                          {user.attributes.delegation?.name}
                        </td>
                        <td className='whitespace-nowrap px-6 py-4 text-sm text-gray-500 hover:text-orange-500'>
                          <IconLink orangeTxt href={`/utenti/modifica/${user.id}`}>
                            <PencilIcon className='w-6 h-6' />
                          </IconLink>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })
            .with({ status: 'idle' }, { status: 'loading' }, () => (
              <p>Caricamento Utenti...</p>
            ))
            .with({ status: 'error' }, () => (
              <p>Errore caricamento Utenti...</p>
            ))
            .exhaustive()}
        </div>
      </div>
    </div>
  );
};

export default AnzianitaTable;
